/**
 * Shared blob → object-URL helpers for gallery cards and chat generated images.
 *
 * URLs are reference-counted so LRU eviction never revokes a URL still in use.
 * Call `releaseBlobObjectUrl` from component cleanup (or use `useBlobObjectUrl`
 * which does create/revoke per mount without the shared map).
 *
 * Prefer `useBlobObjectUrl` for mounted UI. Use this map only when a URL must
 * outlive a single mount or be shared across components for the same key.
 */

const DEFAULT_MAX_ENTRIES = 40;

type CacheEntry = {
  url: string;
  refs: number;
};

const cache = new Map<string, CacheEntry>();

function revokeEntry(key: string, entry: CacheEntry): void {
  URL.revokeObjectURL(entry.url);
  cache.delete(key);
}

function trimIdleEntries(maxEntries: number): void {
  if (cache.size <= maxEntries) {
    return;
  }

  for (const [key, entry] of cache) {
    if (cache.size <= maxEntries) {
      break;
    }
    if (entry.refs === 0) {
      revokeEntry(key, entry);
    }
  }
}

/**
 * Acquire (or reuse) an object URL for `key`. Increments the ref count.
 * Evicts idle (refs === 0) entries when over `maxEntries`.
 */
export function acquireBlobObjectUrl(
  key: string,
  blob: Blob,
  maxEntries: number = DEFAULT_MAX_ENTRIES,
): string {
  const existing = cache.get(key);
  if (existing) {
    existing.refs += 1;
    // Refresh LRU order.
    cache.delete(key);
    cache.set(key, existing);
    return existing.url;
  }

  const url = URL.createObjectURL(blob);
  cache.set(key, { url, refs: 1 });
  trimIdleEntries(maxEntries);
  return url;
}

/**
 * Decrement the ref count for `key`. Revokes and removes the entry when
 * refs reach 0.
 */
export function releaseBlobObjectUrl(key: string): void {
  const entry = cache.get(key);
  if (!entry) {
    return;
  }

  entry.refs -= 1;
  if (entry.refs <= 0) {
    revokeEntry(key, entry);
  }
}
